import styled, { DefaultTheme } from 'styled-components';
import { Card, Select, Button, Progress } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const dark = (p: { theme?: DefaultTheme }, darkVal: string, lightVal: string): string =>
  p.theme?.mode === 'dark' ? darkVal : lightVal;

export const PageWrapper = styled.div`
  padding: 24px;
  background: ${(p) => dark(p, '#0f1419', '#f5f6f8')};
`;

export const HeaderRow = styled.header`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

export const HeaderLeft = styled.div`
  min-width: 0;
`;

export const PageTitle = styled.h1`
  margin: 0 0 8px 0;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 700;
  background: linear-gradient(90deg, #2563eb, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  text-align: left;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  text-align: left;
`;

export const HeaderRight = styled.div`
  flex-shrink: 0;
`;

export const TimeRangeSelect = styled(Select)`
  min-width: 200px;
  border-radius: 12px;

  &.ant-select .ant-select-selector {
    border-radius: 12px;
    background: ${(p) => dark(p, '#1e293b', '#ffffff')} !important;
    border-color: ${(p) => dark(p, '#334155', '#e2e8f0')} !important;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')} !important;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;

export const StatCard = styled(Card)<{ $selected?: boolean }>`
  border-radius: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: ${(p) => (p.$selected ? 'pointer' : 'default')};

  &.ant-card {
    background: ${(p) => (p.$selected ? dark(p, '#1e3a5f', '#eff6ff') : dark(p, '#1e293b', '#ffffff'))};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${(p) => (p.theme?.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)')};
  }
`;

export const StatCardBody = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StatIconWrap = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

export const StatContent = styled.div`
  min-width: 0;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  margin-top: 4px;
`;

export const StatHelp = styled.div<{ $color: string }>`
  font-size: 0.8125rem;
  color: ${(p) => p.$color};
  margin-top: 2px;
`;

export const SectionCard = styled(Card)`
  border-radius: 16px;
  margin-bottom: 24px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SectionIconWrap = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${(p) => p.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const SessionsCard = styled(Card)`
  border-radius: 16px;
  overflow: hidden;
  margin-top: 24px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const SessionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SessionsTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TableWrapper = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    background: ${(p) => dark(p, '#334155', '#f1f5f9')} !important;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')} !important;
    font-weight: 600;
    font-size: 0.8125rem;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    color: ${(p) => dark(p, '#e2e8f0', '#334155')};
  }
  .ant-table-wrapper .ant-table-tbody > tr:hover > td {
    background: ${(p) => dark(p, '#334155', '#f8fafc')} !important;
  }
`;

export const EmptySessionsWrap = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

export const EmptySessionsIcon = styled.div`
  margin-bottom: 12px;
  color: ${(p) => dark(p, '#64748b', '#94a3b8')};
  font-size: 2rem;
  opacity: 0.7;
`;

export const EmptySessionsText = styled.div`
  font-size: 1rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const AnalysisRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const AnalysisLabel = styled.span`
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#e2e8f0', '#334155')};
`;

export const AnalysisProgress = styled(Progress)`
  margin: 16px 0;

  .ant-progress-inner {
    background: ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const AnalysisDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  text-align: center;
  line-height: 1.5;
`;

export const GenerateReportButton = styled(Button)`
  border-radius: 12px;
  font-weight: 500;
`;

export const MerchantBlock = styled.div`
  margin-bottom: 24px;
`;

export const MerchantBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const MerchantSessionCount = styled.span`
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;
